import axios from "axios";
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import DataTable from "../components/DataTable";

import {
  MODULO_COBROS,
  authConfig,
  badgeEstado,
  formatearFecha,
  formatearMoneda,
  manejarErrorSesion,
  nombreInstructor,
  nombreOrigen,
  obtenerModuloPorNombre,
  tienePermiso,
} from "../utils/cobros";

const RUTAS = {
  inicio: "/inicio",

  nueva:
    "/cobros/suscripciones/nueva",

  editar: (id) =>
    `/cobros/suscripciones/edit/${id}`,

  pagoSuscripcion: (id) =>
    `/cobros/pagos/nuevo?concepto=SUSCRIPCION&suscripcionId=${id}`,

  pagoRenovacion: (id) =>
    `/cobros/pagos/nuevo?concepto=RENOVACION&suscripcionId=${id}`,
};

const obtenerId = (valor) => {
  if (!valor) return "";

  if (
    typeof valor === "object" &&
    valor._id
  ) {
    return String(valor._id);
  }

  return String(valor);
};

const estadoParaMostrar = (estado) => {
  const valor = String(
    estado || ""
  ).toUpperCase();

  // Compatibilidad con registros antiguos.
  if (valor === "PENDIENTE") {
    return "PROGRAMADA";
  }

  return valor;
};

const badgeEstadoSuscripcion = (
  estado
) => {
  const estadoVisual =
    estadoParaMostrar(estado);

  if (
    estadoVisual ===
    "PENDIENTE_PAGO"
  ) {
    return (
      <span className="badge rounded-pill text-bg-warning">
        PENDIENTE PAGO
      </span>
    );
  }

  if (
    estadoVisual ===
    "PROGRAMADA"
  ) {
    return (
      <span className="badge rounded-pill text-bg-info">
        PROGRAMADA
      </span>
    );
  }

  return badgeEstado(
    estadoVisual
  );
};

const ListarSuscripciones = () => {
  const navigate = useNavigate();

  const [
    suscripciones,
    setSuscripciones,
  ] = useState([]);

  const [
    loading,
    setLoading,
  ] = useState(false);

  const [
    procesandoId,
    setProcesandoId,
  ] = useState(null);

  const modulo = useMemo(
    () =>
      obtenerModuloPorNombre(
        MODULO_COBROS
      ),
    []
  );

  const puedeLeer =
    useMemo(
      () =>
        tienePermiso(
          modulo,
          "LEER"
        ),
      [modulo]
    );

  const puedeCrear =
    useMemo(
      () =>
        tienePermiso(
          modulo,
          "CREAR"
        ),
      [modulo]
    );

  const puedeActualizar =
    useMemo(
      () =>
        tienePermiso(
          modulo,
          "ACTUALIZAR"
        ),
      [modulo]
    );

  const puedeEliminar =
    useMemo(
      () =>
        tienePermiso(
          modulo,
          "ELIMINAR"
        ),
      [modulo]
    );

  const getData =
    useCallback(async () => {
      if (!puedeLeer) {
        setSuscripciones([]);
        return;
      }

      try {
        setLoading(true);

        const { data } =
          await axios.get(
            "/api/suscripciones/listall",
            authConfig()
          );

        if (
          !Array.isArray(
            data?.suscripciones
          )
        ) {
          throw new Error(
            "Datos de suscripciones no válidos."
          );
        }

        setSuscripciones(
          data.suscripciones
        );
      } catch (error) {
        console.error(
          "Error cargando suscripciones:",
          error
        );

        if (
          manejarErrorSesion(
            error,
            navigate
          )
        ) {
          return;
        }

        Swal.fire(
          "Error",
          error.response?.data
            ?.message ||
            "No se pudieron cargar las suscripciones.",
          "error"
        );

        setSuscripciones([]);
      } finally {
        setLoading(false);
      }
    }, [
      navigate,
      puedeLeer,
    ]);

  useEffect(() => {
    getData();
  }, [getData]);

  const ejecutarAccion =
    useCallback(
      async (
        suscripcion,
        accion
      ) => {
        if (!puedeActualizar) {
          Swal.fire(
            "Sin permisos",
            "No tiene permiso para actualizar suscripciones.",
            "warning"
          );

          return;
        }

        const textos = {
          suspender: {
            titulo:
              "Suspender suscripción",

            texto:
              "El acceso quedará suspendido hasta una reactivación administrativa.",
          },

          reactivar: {
            titulo:
              "¿Reactivar suscripción?",

            texto:
              "El sistema recalculará el estado según la vigencia.",
          },

          cancelar: {
            titulo:
              "Cancelar suscripción",

            texto:
              "La cancelación impide seguir utilizando este período.",
          },
        };

        if (!textos[accion]) {
          return;
        }

        let motivo = "";

        if (
          [
            "suspender",
            "cancelar",
          ].includes(accion)
        ) {
          const result =
            await Swal.fire({
              title:
                textos[accion]
                  .titulo,

              text:
                textos[accion]
                  .texto,

              input:
                "textarea",

              inputLabel:
                accion ===
                "suspender"
                  ? "Motivo de suspensión"
                  : "Motivo de cancelación",

              inputPlaceholder:
                "Escriba el motivo...",

              icon:
                "warning",

              showCancelButton:
                true,

              confirmButtonText:
                accion ===
                "suspender"
                  ? "Suspender"
                  : "Cancelar suscripción",

              cancelButtonText:
                "Volver",

              inputValidator: (
                value
              ) =>
                !String(
                  value || ""
                ).trim()
                  ? "Indique el motivo."
                  : undefined,
            });

          if (
            !result.isConfirmed
          ) {
            return;
          }

          motivo = String(
            result.value || ""
          ).trim();
        } else {
          const result =
            await Swal.fire({
              title:
                textos[accion]
                  .titulo,

              text:
                textos[accion]
                  .texto,

              icon:
                "question",

              showCancelButton:
                true,

              confirmButtonText:
                "Continuar",

              cancelButtonText:
                "Cancelar",
            });

          if (
            !result.isConfirmed
          ) {
            return;
          }
        }

        try {
          setProcesandoId(
            suscripcion._id
          );

          const payload =
            accion ===
            "suspender"
              ? {
                  motivoSuspension:
                    motivo,
                }
              : accion ===
                  "cancelar"
                ? {
                    motivoCancelacion:
                      motivo,
                  }
                : {};

          const { data } =
            await axios.put(
              `/api/suscripciones/${accion}/${suscripcion._id}`,
              payload,
              authConfig()
            );

          await Swal.fire({
            icon:
              "success",

            title:
              data?.message ||
              "Operación realizada correctamente",

            showConfirmButton:
              false,

            timer:
              1600,
          });

          await getData();
        } catch (error) {
          console.error(
            `Error al ${accion} suscripción:`,
            error
          );

          if (
            manejarErrorSesion(
              error,
              navigate
            )
          ) {
            return;
          }

          Swal.fire(
            "Error",
            error.response?.data
              ?.message ||
              "No se pudo realizar la operación.",
            "error"
          );
        } finally {
          setProcesandoId(
            null
          );
        }
      },
      [
        getData,
        navigate,
        puedeActualizar,
      ]
    );

  const eliminarSuscripcion =
    useCallback(
      async (
        suscripcion
      ) => {
        if (!puedeEliminar) {
          Swal.fire(
            "Sin permisos",
            "No tiene permisos para eliminar suscripciones.",
            "warning"
          );

          return;
        }

        const result =
          await Swal.fire({
            title:
              "¿Eliminar suscripción?",

            text:
              "Solo deben eliminarse registros pendientes de pago o cancelados que no tengan pagos aprobados.",

            icon:
              "warning",

            showCancelButton:
              true,

            confirmButtonText:
              "Sí, eliminar",

            cancelButtonText:
              "Cancelar",

            confirmButtonColor:
              "#dc3545",
          });

        if (
          !result.isConfirmed
        ) {
          return;
        }

        try {
          setProcesandoId(
            suscripcion._id
          );

          const { data } =
            await axios.delete(
              `/api/suscripciones/delete/${suscripcion._id}`,
              authConfig()
            );

          await Swal.fire({
            icon:
              "success",

            title:
              data?.message ||
              "Suscripción eliminada",

            showConfirmButton:
              false,

            timer:
              1600,
          });

          await getData();
        } catch (error) {
          console.error(
            "Error eliminando suscripción:",
            error
          );

          if (
            manejarErrorSesion(
              error,
              navigate
            )
          ) {
            return;
          }

          Swal.fire(
            "Error",
            error.response?.data
              ?.message ||
              "No se pudo eliminar la suscripción.",
            "error"
          );
        } finally {
          setProcesandoId(
            null
          );
        }
      },
      [
        getData,
        navigate,
        puedeEliminar,
      ]
    );

  const actualizarEstados =
    useCallback(async () => {
      if (!puedeActualizar) {
        return;
      }

      try {
        setLoading(true);

        const { data } =
          await axios.put(
            "/api/suscripciones/actualizarestados",
            {},
            authConfig()
          );

        await Swal.fire({
          icon:
            "success",

          title:
            data?.message ||
            "Estados verificados",

          text:
            `Revisadas: ${
              data?.totalRevisadas ||
              0
            } · Actualizadas: ${
              data?.totalActualizadas ||
              0
            }`,
        });

        await getData();
      } catch (error) {
        if (
          manejarErrorSesion(
            error,
            navigate
          )
        ) {
          return;
        }

        Swal.fire(
          "Error",
          error.response?.data
            ?.message ||
            "No se pudieron verificar los estados.",
          "error"
        );
      } finally {
        setLoading(false);
      }
    }, [
      getData,
      navigate,
      puedeActualizar,
    ]);

  const renovacionesProgramadas =
    useMemo(() => {
      const ids =
        new Set();

      suscripciones.forEach(
        (
          suscripcion
        ) => {
          const estado =
            estadoParaMostrar(
              suscripcion
                .estadoSuscripcion
            );

          const origen =
            String(
              suscripcion
                .origenSuscripcion ||
                ""
            ).toUpperCase();

          if (
            origen !==
              "RENOVACION" ||
            estado !==
              "PROGRAMADA"
          ) {
            return;
          }

          const origenId =
            obtenerId(
              suscripcion
                .suscripcionOrigenObjectId
            );

          if (origenId) {
            ids.add(
              origenId
            );
          }
        }
      );

      return ids;
    }, [suscripciones]);

  const dataTabla =
    useMemo(
      () =>
        suscripciones.map(
          (
            suscripcion
          ) => {
            const instructor =
              suscripcion
                .instructorObjectId;

            const plan =
              suscripcion
                .planObjectId;

            const estadoReal =
              String(
                suscripcion
                  .estadoSuscripcion ||
                  ""
              ).toUpperCase();

            const estado =
              estadoParaMostrar(
                estadoReal
              );

            const origen =
              String(
                suscripcion
                  .origenSuscripcion ||
                  ""
              ).toUpperCase();

            const generadaPorPago =
              [
                "PAGO",
                "RENOVACION",
              ].includes(
                origen
              ) &&
              Boolean(
                suscripcion
                  .pagoObjectId
              );

            const yaTieneRenovacionProgramada =
              renovacionesProgramadas.has(
                String(
                  suscripcion._id
                )
              );

            const planPermiteRenovar =
              plan
                ?.permiteRenovacion !==
              false;

            const estadoPermiteRenovar =
              [
                "ACTIVA",
                "POR_VENCER",
                "GRACIA",
                "VENCIDA",
              ].includes(
                estado
              );

            const puedeRenovar =
              estadoPermiteRenovar &&
              planPermiteRenovar &&
              !yaTieneRenovacionProgramada;

            const puedeEditarRegistro =
              estado ===
                "PENDIENTE_PAGO" ||
              !generadaPorPago ||
              [
                "PRUEBA",
                "CORTESIA",
                "ADMINISTRADOR",
              ].includes(
                origen
              );

            return {
              ...suscripcion,

              documentoInstructor:
                instructor
                  ?.documentoInstructor ||
                "—",

              instructorNombre:
                nombreInstructor(
                  instructor
                ),

              planNombre:
                plan?.nombrePlan ||
                "—",

              origenVisual:
                nombreOrigen(
                  origen
                ),

              valorPlan:
                formatearMoneda(
                  plan?.valorPlan,
                  plan?.moneda
                ),

              fechaInicioFormateada:
                formatearFecha(
                  suscripcion
                    .fechaInicio
                ),

              fechaFinFormateada:
                formatearFecha(
                  suscripcion
                    .fechaFin
                ),

              fechaFinGraciaFormateada:
                formatearFecha(
                  suscripcion
                    .fechaFinGracia
                ),

              estadoVisual:
                badgeEstadoSuscripcion(
                  estado
                ),

              funciones: (
                <div className="d-flex justify-content-end align-items-center gap-2 flex-wrap">

                  {puedeActualizar &&
                    estado ===
                      "PENDIENTE_PAGO" && (
                      <button
                        className="btn btn-success btn-sm d-inline-flex align-items-center justify-content-center"
                        style={{
                          width:
                            36,
                          height:
                            36,
                        }}
                        type="button"
                        title="Registrar pago de suscripción"
                        onClick={() =>
                          navigate(
                            RUTAS.pagoSuscripcion(
                              suscripcion._id
                            )
                          )
                        }
                      >
                        <i className="fa-solid fa-money-bill-wave"></i>
                      </button>
                    )}

                  {puedeActualizar &&
                    puedeRenovar && (
                      <button
                        className="btn btn-outline-success btn-sm d-inline-flex align-items-center justify-content-center"
                        style={{
                          width:
                            36,
                          height:
                            36,
                        }}
                        type="button"
                        title="Renovar suscripción"
                        onClick={() =>
                          navigate(
                            RUTAS.pagoRenovacion(
                              suscripcion._id
                            )
                          )
                        }
                      >
                        <i className="fa-solid fa-arrows-rotate"></i>
                      </button>
                    )}

                  {puedeActualizar &&
                    yaTieneRenovacionProgramada &&
                    [
                      "ACTIVA",
                      "POR_VENCER",
                      "GRACIA",
                    ].includes(
                      estado
                    ) && (
                      <button
                        className="btn btn-outline-info btn-sm d-inline-flex align-items-center justify-content-center"
                        style={{
                          width:
                            36,
                          height:
                            36,
                        }}
                        type="button"
                        title="Ya existe una renovación programada"
                        disabled
                      >
                        <i className="fa-solid fa-calendar-check"></i>
                      </button>
                    )}

                  {puedeActualizar &&
                    puedeEditarRegistro && (
                      <button
                        className="btn btn-warning btn-sm d-inline-flex align-items-center justify-content-center"
                        style={{
                          width:
                            36,
                          height:
                            36,
                        }}
                        type="button"
                        title="Editar"
                        onClick={() =>
                          navigate(
                            RUTAS.editar(
                              suscripcion._id
                            )
                          )
                        }
                      >
                        <i className="fa-solid fa-pen-to-square"></i>
                      </button>
                    )}

                  {puedeActualizar &&
                    [
                      "ACTIVA",
                      "POR_VENCER",
                      "GRACIA",
                    ].includes(
                      estado
                    ) && (
                      <button
                        className="btn btn-outline-danger btn-sm d-inline-flex align-items-center justify-content-center"
                        style={{
                          width:
                            36,
                          height:
                            36,
                        }}
                        type="button"
                        title="Suspender"
                        onClick={() =>
                          ejecutarAccion(
                            suscripcion,
                            "suspender"
                          )
                        }
                        disabled={
                          procesandoId ===
                          suscripcion._id
                        }
                      >
                        <i className="fa-solid fa-pause"></i>
                      </button>
                    )}

                  {puedeActualizar &&
                    estado ===
                      "SUSPENDIDA" && (
                      <button
                        className="btn btn-success btn-sm d-inline-flex align-items-center justify-content-center"
                        style={{
                          width:
                            36,
                          height:
                            36,
                        }}
                        type="button"
                        title="Reactivar"
                        onClick={() =>
                          ejecutarAccion(
                            suscripcion,
                            "reactivar"
                          )
                        }
                        disabled={
                          procesandoId ===
                          suscripcion._id
                        }
                      >
                        <i className="fa-solid fa-rotate-right"></i>
                      </button>
                    )}

                  {puedeActualizar &&
                    estado !==
                      "CANCELADA" && (
                      <button
                        className="btn btn-secondary btn-sm d-inline-flex align-items-center justify-content-center"
                        style={{
                          width:
                            36,
                          height:
                            36,
                        }}
                        type="button"
                        title="Cancelar"
                        onClick={() =>
                          ejecutarAccion(
                            suscripcion,
                            "cancelar"
                          )
                        }
                        disabled={
                          procesandoId ===
                          suscripcion._id
                        }
                      >
                        <i className="fa-solid fa-ban"></i>
                      </button>
                    )}

                  {puedeEliminar &&
                    [
                      "PENDIENTE_PAGO",
                      "CANCELADA",
                    ].includes(
                      estado
                    ) && (
                      <button
                        className="btn btn-danger btn-sm d-inline-flex align-items-center justify-content-center"
                        style={{
                          width:
                            36,
                          height:
                            36,
                        }}
                        type="button"
                        title="Eliminar"
                        onClick={() =>
                          eliminarSuscripcion(
                            suscripcion
                          )
                        }
                        disabled={
                          procesandoId ===
                          suscripcion._id
                        }
                      >
                        <i
                          className={`fa-solid ${
                            procesandoId ===
                            suscripcion._id
                              ? "fa-spinner fa-spin"
                              : "fa-trash"
                          }`}
                        ></i>
                      </button>
                    )}
                </div>
              ),
            };
          }
        ),
      [
        suscripciones,
        renovacionesProgramadas,
        puedeActualizar,
        puedeEliminar,
        navigate,
        ejecutarAccion,
        eliminarSuscripcion,
        procesandoId,
      ]
    );

  const columns = useMemo(
    () => [
      {
        Header:
          "Documento",
        accessor:
          "documentoInstructor",
      },

      {
        Header:
          "Instructor",
        accessor:
          "instructorNombre",
      },

      {
        Header:
          "Plan",
        accessor:
          "planNombre",
      },

      {
        Header:
          "Origen",
        accessor:
          "origenVisual",
      },

      {
        Header:
          "Valor",
        accessor:
          "valorPlan",
      },

      {
        Header:
          "Inicio",
        accessor:
          "fechaInicioFormateada",
      },

      {
        Header:
          "Fin",
        accessor:
          "fechaFinFormateada",
      },

      {
        Header:
          "Fin gracia",

        accessor:
          "fechaFinGraciaFormateada",

        hideHeaderOnMobile:
          true,
      },

      {
        Header:
          "Estado",
        accessor:
          "estadoVisual",
      },

      {
        Header:
          "Acciones",
        accessor:
          "funciones",
      },
    ],
    []
  );

  if (!puedeLeer) {
    return (
      <div className="container-fluid px-2 px-md-4">
        <div className="bg-white border rounded-4 shadow-sm p-4">

          <div className="alert alert-warning">
            No tiene permisos para consultar suscripciones.
          </div>

          <button
            className="btn btn-dark"
            type="button"
            onClick={() =>
              navigate(
                RUTAS.inicio
              )
            }
          >
            Volver al inicio
          </button>

        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid px-2 px-md-4">

      <div className="bg-white border rounded-4 shadow-sm overflow-hidden">

        <div className="px-3 px-md-4 py-3 border-bottom bg-white">

          <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">

            <div>
              <h5 className="mt-2 mb-1 fw-semibold">
                Suscripciones
              </h5>

              <small className="text-secondary d-block">
                Controle las suscripciones activas, pendientes de pago y renovaciones programadas.
              </small>

              <div className="mt-3 d-flex flex-wrap gap-2">

                <span className="badge rounded-pill text-bg-warning">
                  PENDIENTE PAGO
                </span>

                <span className="small text-secondary me-3">
                  Falta confirmar el pago.
                </span>

                <span className="badge rounded-pill text-bg-info">
                  PROGRAMADA
                </span>

                <span className="small text-secondary">
                  Pago confirmado; la vigencia inicia posteriormente.
                </span>

              </div>
            </div>

            <div className="d-flex gap-2 flex-wrap">

              <button
                className="btn btn-outline-dark"
                type="button"
                onClick={() =>
                  navigate(
                    RUTAS.inicio
                  )
                }
              >
                <i className="fa-solid fa-house me-2"></i>
                Inicio
              </button>

              <button
                className="btn btn-outline-secondary"
                type="button"
                onClick={
                  getData
                }
                disabled={
                  loading
                }
              >
                <i
                  className={`fa-solid ${
                    loading
                      ? "fa-spinner fa-spin"
                      : "fa-rotate"
                  } me-2`}
                ></i>

                Actualizar
              </button>

              {puedeActualizar && (
                <button
                  className="btn btn-outline-primary"
                  type="button"
                  onClick={
                    actualizarEstados
                  }
                  disabled={
                    loading
                  }
                >
                  <i className="fa-solid fa-clock-rotate-left me-2"></i>

                  Verificar estados
                </button>
              )}

              {puedeCrear && (
                <button
                  className="btn btn-dark"
                  type="button"
                  onClick={() =>
                    navigate(
                      RUTAS.nueva
                    )
                  }
                  disabled={
                    loading
                  }
                >
                  <i className="fa-solid fa-plus me-2"></i>

                  Nueva suscripción
                </button>
              )}

            </div>
          </div>
        </div>

        <div>
          {loading ? (
            <div className="text-center text-secondary py-5">

              <div
                className="spinner-border"
                role="status"
              ></div>

              <div className="mt-3">
                Cargando suscripciones…
              </div>

            </div>
          ) : dataTabla.length > 0 ? (
            <div className="overflow-auto">
              <DataTable
                columns={
                  columns
                }
                data={
                  dataTabla
                }
              />
            </div>
          ) : (
            <div className="text-center text-secondary py-5 px-3">

              <div className="display-6 mb-2">
                🔐
              </div>

              <div className="fw-semibold">
                No hay suscripciones registradas
              </div>

              <div className="small mb-3">
                Registre una suscripción. Si es comercial quedará pendiente de pago hasta su aprobación.
              </div>

              {puedeCrear && (
                <button
                  className="btn btn-dark"
                  type="button"
                  onClick={() =>
                    navigate(
                      RUTAS.nueva
                    )
                  }
                >
                  <i className="fa-solid fa-plus me-2"></i>

                  Registrar suscripción
                </button>
              )}

            </div>
          )}
        </div>

      </div>
    </div>
  );
};

export default ListarSuscripciones;